import { useEffect, useCallback } from 'react';

/**
 * iOS Keyboard Detection Hook
 * 
 * Detects when the iOS virtual keyboard is visible and applies appropriate
 * CSS classes to handle mobile navigation and content layout adjustments.
 * 
 * Features:
 * - Viewport height monitoring for keyboard detection
 * - CSS class management for layout adjustments
 * - PWA mode compatibility
 * - Performance optimized with debouncing
 */

interface KeyboardState {
  isVisible: boolean;
  height: number;
  viewportHeight: number;
}

export function useIOSKeyboardDetection() {
  // Detect if we're on iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Debounce function for performance
  const debounce = useCallback((func: (...args: any[]) => void, wait: number) => {
    let timeout: NodeJS.Timeout;
    return function executedFunction(...args: any[]) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }, []);

  // Handle keyboard state changes
  const handleKeyboardChange = useCallback((state: KeyboardState) => {
    const body = document.body;
    const root = document.documentElement;
    
    if (state.isVisible) {
      // Keyboard is visible
      body.classList.add('ios-keyboard-detected');
      root.style.setProperty('--keyboard-height', `${state.height}px`);
      root.style.setProperty('--viewport-height', `${state.viewportHeight}px`);
    } else {
      // Keyboard is hidden
      body.classList.remove('ios-keyboard-detected');
      root.style.setProperty('--keyboard-height', '0px');
      root.style.setProperty('--viewport-height', '100vh');
    }
  }, []);

  // Debounced keyboard change handler
  const debouncedKeyboardChange = useCallback(
    debounce(handleKeyboardChange, 150),
    [handleKeyboardChange, debounce]
  );

  useEffect(() => {
    if (!isIOS) return;

    let initialViewportHeight = window.visualViewport?.height || window.innerHeight;
    let currentKeyboardState: KeyboardState = {
      isVisible: false,
      height: 0,
      viewportHeight: initialViewportHeight
    };

    // Visual Viewport API (preferred method for iOS 13+)
    const handleVisualViewportChange = () => {
      if (!window.visualViewport) return;

      const currentHeight = window.visualViewport.height;
      const keyboardHeight = initialViewportHeight - currentHeight;
      const isKeyboardVisible = keyboardHeight > 150; // Threshold for keyboard detection

      const newState: KeyboardState = {
        isVisible: isKeyboardVisible,
        height: Math.max(0, keyboardHeight),
        viewportHeight: currentHeight
      };

      // Only update if state actually changed
      if (newState.isVisible !== currentKeyboardState.isVisible ||
          Math.abs(newState.height - currentKeyboardState.height) > 10) {
        currentKeyboardState = newState;
        debouncedKeyboardChange(newState);
      }
    };

    // Fallback method using window resize (for older iOS versions)
    const handleWindowResize = () => {
      if (window.visualViewport) return; // Skip if Visual Viewport API is available

      const currentHeight = window.innerHeight;
      const keyboardHeight = initialViewportHeight - currentHeight;
      const isKeyboardVisible = keyboardHeight > 150;

      const newState: KeyboardState = {
        isVisible: isKeyboardVisible,
        height: Math.max(0, keyboardHeight),
        viewportHeight: currentHeight
      };

      if (newState.isVisible !== currentKeyboardState.isVisible ||
          Math.abs(newState.height - currentKeyboardState.height) > 10) {
        currentKeyboardState = newState;
        debouncedKeyboardChange(newState);
      }
    };

    // Input focus/blur handlers for additional keyboard detection
    const handleInputFocus = () => {
      // Small delay to allow keyboard to appear
      setTimeout(() => {
        if (window.visualViewport) {
          handleVisualViewportChange();
        } else {
          handleWindowResize();
        }
      }, 300);
    };

    const handleInputBlur = () => {
      // Small delay to allow keyboard to disappear
      setTimeout(() => {
        if (window.visualViewport) {
          handleVisualViewportChange();
        } else {
          handleWindowResize();
        }
      }, 300);
    };

    // Set up event listeners
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportChange);
    } else {
      window.addEventListener('resize', handleWindowResize);
    }

    // Add input focus/blur listeners to all input elements
    const inputs = document.querySelectorAll('input, textarea, select');
    inputs.forEach(input => {
      input.addEventListener('focus', handleInputFocus);
      input.addEventListener('blur', handleInputBlur);
    });

    // Observer for dynamically added inputs
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            const newInputs = element.querySelectorAll('input, textarea, select');
            newInputs.forEach(input => {
              input.addEventListener('focus', handleInputFocus);
              input.addEventListener('blur', handleInputBlur);
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup function
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportChange);
      } else {
        window.removeEventListener('resize', handleWindowResize);
      }

      // Remove input listeners
      const allInputs = document.querySelectorAll('input, textarea, select');
      allInputs.forEach(input => {
        input.removeEventListener('focus', handleInputFocus);
        input.removeEventListener('blur', handleInputBlur);
      });

      observer.disconnect();

      // Clean up CSS classes and variables
      document.body.classList.remove('ios-keyboard-detected');
      document.documentElement.style.removeProperty('--keyboard-height');
      document.documentElement.style.removeProperty('--viewport-height');
    };
  }, [isIOS, debouncedKeyboardChange]);

  return {
    isIOS,
    isSupported: isIOS && (!!window.visualViewport || true) // Always supported with fallback
  };
}

/**
 * Enhanced iOS Keyboard Detection with Form Integration
 *
 * This hook provides additional utilities for form handling with keyboard detection
 */
export function useIOSKeyboardWithForms() {
  const { isIOS, isSupported } = useIOSKeyboardDetection();

  // Scroll to input when keyboard appears
  const scrollToInput = useCallback((inputElement: HTMLElement) => {
    if (!isIOS) return;

    setTimeout(() => {
      const rect = inputElement.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const keyboardHeight = (window.screen.height || window.innerHeight) - viewportHeight;

      if (keyboardHeight > 150) {
        // Keyboard is visible, scroll to keep input visible
        const scrollTop = window.pageYOffset;
        const inputTop = rect.top + scrollTop;
        const targetScroll = inputTop - (viewportHeight - keyboardHeight) / 2;

        window.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth'
        });
      }
    }, 300); // Wait for keyboard animation
  }, [isIOS]);

  // Auto-scroll on input focus
  useEffect(() => {
    if (!isIOS) return;

    const handleInputFocus = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT')) {
        scrollToInput(target);
      }
    };

    document.addEventListener('focusin', handleInputFocus);
    return () => document.removeEventListener('focusin', handleInputFocus);
  }, [isIOS, scrollToInput]);

  return {
    isIOS,
    isSupported,
    scrollToInput
  };
}
